import { changePasswordSchema, THEME_VALUES, type Theme } from '@ifretes/shared'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { Botao, Campo, ErroApi, Mensagem } from '../../components/ui'
import { api, ApiError, requisitar } from '../../lib/api'
import { useTheme } from '../../theme/ThemeProvider'
import { useAuth } from '../auth/store'

interface Sessao {
  id: string
  current: boolean
  ip: string | null
  userAgent: string | null
  createdAt: string
  lastUsedAt: string
}

const ROTULO_TEMA: Record<Theme, { titulo: string; descricao: string; icone: string }> = {
  light: { titulo: 'Claro', descricao: 'Sempre claro', icone: 'fa-sun' },
  dark: { titulo: 'Escuro', descricao: 'Sempre escuro', icone: 'fa-moon' },
  system: { titulo: 'Sistema', descricao: 'Segue o aparelho', icone: 'fa-adjust' },
}

function descreverDispositivo(ua: string | null): string {
  if (!ua) return 'Dispositivo desconhecido'
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad|iOS/i.test(ua)) return 'iPhone ou iPad'
  if (/Windows/i.test(ua)) return 'Windows'
  if (/Macintosh|Mac OS/i.test(ua)) return 'macOS'
  if (/Linux/i.test(ua)) return 'Linux'
  if (/curl|Postman|insomnia/i.test(ua)) return 'Ferramenta de API'
  return 'Outro dispositivo'
}

export function MinhaConta() {
  const { usuario, definirUsuario } = useAuth()
  const { theme, resolved, setTheme } = useTheme()

  const [sessoes, setSessoes] = useState<Sessao[]>([])
  const [carregandoSessoes, setCarregandoSessoes] = useState(true)

  const [senhaAtual, setSenhaAtual] = useState('')
  const [senhaNova, setSenhaNova] = useState('')
  const [trocando, setTrocando] = useState(false)
  const [erroSenha, setErroSenha] = useState<unknown>(null)
  const [camposSenha, setCamposSenha] = useState<Record<string, string[]>>({})
  const [senhaTrocada, setSenhaTrocada] = useState(false)

  async function carregarSessoes() {
    try {
      const s = (await api.sessoes()) as unknown as Sessao[]
      setSessoes(s)
    } catch {
      // Falha ao listar sessões não impede o resto da página de funcionar.
    } finally {
      setCarregandoSessoes(false)
    }
  }

  useEffect(() => {
    void carregarSessoes()
  }, [])

  /** Salva o tema no servidor para acompanhar o login entre dispositivos. */
  async function escolherTema(t: Theme) {
    setTheme(t) // aplica na hora; a persistência remota é secundária
    try {
      await api.salvarPreferencias({ theme: t })
      if (usuario) {
        definirUsuario({ ...usuario, preferences: { ...usuario.preferences, theme: t } })
      }
    } catch {
      // Sem rede a escolha vale localmente; o localStorage já guardou.
    }
  }

  async function trocarSenha(e: React.FormEvent) {
    e.preventDefault()
    setErroSenha(null)
    setCamposSenha({})
    setSenhaTrocada(false)

    const r = changePasswordSchema.safeParse({
      currentPassword: senhaAtual,
      newPassword: senhaNova,
    })
    if (!r.success) {
      const mapa: Record<string, string[]> = {}
      for (const i of r.error.issues) {
        const k = i.path.join('.') || '_'
        ;(mapa[k] ??= []).push(i.message)
      }
      setCamposSenha(mapa)
      return
    }

    setTrocando(true)
    try {
      await requisitar('/me/change-password', { metodo: 'POST', corpo: r.data })
      setSenhaTrocada(true)
      setSenhaAtual('')
      setSenhaNova('')
      // A API encerra todas as sessões ao trocar a senha, então a lista antiga
      // já não vale.
      void carregarSessoes()
    } catch (err) {
      if (err instanceof ApiError) setCamposSenha(err.camposComErro)
      setErroSenha(err)
    } finally {
      setTrocando(false)
    }
  }

  async function encerrar(id: string) {
    try {
      await api.encerrarSessao(id)
      setSessoes((s) => s.filter((x) => x.id !== id))
    } catch {
      void carregarSessoes()
    }
  }

  if (!usuario) return null

  return (
    <div className="ifr-pagina ifr-pagina--estreita">
      <h1 className="ifr-titulo">Minha conta</h1>
      <p className="ifr-apoio">{usuario.email}</p>

      {/* ─────────────────────────────────────────────── aparência */}
      <section className="ifr-mb-g">
        <h2 className="ifr-titulo ifr-titulo--secao">Aparência</h2>
        <p className="ifr-nota ifr-fraco ifr-mb">
          A escolha vale neste aparelho e acompanha seu login nos outros.
        </p>

        <fieldset className="ifr-sem-moldura">
          <legend className="apenas-leitor">Tema da interface</legend>
          {/* `.ifr-escolhas` em vez de `repeat(3, 1fr)` embutido: a grade fixa
              dava ~105px por coluna num celular de 360px e quebrava os rótulos
              letra a letra. A classe usa `auto-fit` com mínimo, então as três
              opções viram duas ou uma conforme a largura, sem media query. */}
          <div className="ifr-escolhas">
            {THEME_VALUES.map((t) => {
              const r = ROTULO_TEMA[t]
              const ativo = theme === t
              return (
                <label key={t} className={`ifr-escolha ${ativo ? 'ifr-escolha--ativa' : ''}`}>
                  <input
                    type="radio"
                    name="tema"
                    checked={ativo}
                    onChange={() => void escolherTema(t)}
                    className="apenas-leitor"
                  />
                  {/* A cor do ícone ativo vem da regra
                      `.ifr-escolha--ativa .ifr-escolha__icone`, não de um
                      ternário aqui: assim o estado tem um lugar só. */}
                  <i className={`fas ${r.icone} ifr-escolha__icone`} aria-hidden="true" />
                  <span className="ifr-escolha__titulo">{r.titulo}</span>
                  <span className="ifr-mini ifr-fraco">
                    {t === 'system'
                      ? `${r.descricao} (${resolved === 'dark' ? 'escuro' : 'claro'})`
                      : r.descricao}
                  </span>
                </label>
              )
            })}
          </div>
        </fieldset>
      </section>

      {/* ─────────────────────────────────────────────── segurança */}
      <section className="ifr-mb-g">
        <h2 className="ifr-titulo ifr-titulo--secao ifr-mb">Segurança</h2>

        <div className="ifr-cartao ifr-linha ifr-mb">
          {/* A cor diz o estado do 2FA e vem por classe, não por `style`: as
              variantes trocam fundo E glifo juntos, e o tema escuro continua
              no controle das duas cores. */}
          <i
            className={`fas fa-shield-alt ifr-icone-bloco ${
              usuario.mfaEnabled ? 'ifr-icone-bloco--sucesso' : 'ifr-icone-bloco--atencao'
            }`}
            aria-hidden="true"
          />
          <div className="ifr-crescer-min">
            <div className="ifr-forte">Verificação em dois fatores</div>
            <div className="ifr-nota ifr-fraco">
              {usuario.mfaEnabled
                ? 'Ativa. O código é pedido a cada login.'
                : 'Inativa. Sua conta depende apenas da senha.'}
            </div>
          </div>
          {!usuario.mfaEnabled && (
            <Link to="/minha-conta/mfa" className="br-button primary small">
              Ativar
            </Link>
          )}
        </div>

        <form onSubmit={trocarSenha} className="ifr-pilha ifr-largura-forma">
          <h3 className="ifr-titulo ifr-titulo--bloco ifr-forte-cor">Trocar senha</h3>

          {senhaTrocada && (
            <Mensagem tipo="success" titulo="Senha alterada">
              Todas as sessões foram encerradas. Você continuará nesta até recarregar a página.
            </Mensagem>
          )}
          <ErroApi erro={erroSenha} />

          <Campo
            id="senha-atual"
            rotulo="Senha atual"
            tipo="password"
            valor={senhaAtual}
            aoMudar={setSenhaAtual}
            erros={camposSenha.currentPassword}
            obrigatorio
            autoComplete="current-password"
          />
          <Campo
            id="senha-nova"
            rotulo="Nova senha"
            tipo="password"
            valor={senhaNova}
            aoMudar={setSenhaNova}
            erros={camposSenha.newPassword}
            obrigatorio
            autoComplete="new-password"
          />
          <Botao tipo="submit" carregando={trocando}>
            Trocar senha
          </Botao>
        </form>
      </section>

      {/* ─────────────────────────────────────────────── sessões */}
      <section>
        <h2 className="ifr-titulo ifr-titulo--secao">Sessões ativas</h2>
        <p className="ifr-nota ifr-fraco ifr-mb">
          Onde sua conta está conectada. Encerre o que não reconhecer.
        </p>

        {carregandoSessoes ? (
          <p className="ifr-fraco">Carregando…</p>
        ) : sessoes.length === 0 ? (
          <p className="ifr-fraco">Nenhuma sessão ativa além desta.</p>
        ) : (
          <ul className="ifr-lista ifr-pilha ifr-pilha--curta">
            {sessoes.map((s) => (
              <li key={s.id} className="ifr-cartao ifr-cartao--compacto ifr-linha">
                <i className="fas fa-desktop ifr-mais-fraco" aria-hidden="true" />
                <div className="ifr-crescer-min">
                  <div className="ifr-forte">
                    {descreverDispositivo(s.userAgent)}
                    {s.current && (
                      <span className="ifr-tag ifr-tag--sucesso ifr-contador">esta sessão</span>
                    )}
                  </div>
                  <div className="ifr-mini ifr-fraco">
                    {s.ip ?? 'IP desconhecido'} · último uso{' '}
                    {new Date(s.lastUsedAt).toLocaleString('pt-BR')}
                  </div>
                </div>
                {!s.current && (
                  <button
                    className="br-button secondary small"
                    onClick={() => void encerrar(s.id)}
                    aria-label={`Encerrar sessão em ${descreverDispositivo(s.userAgent)}`}
                  >
                    Encerrar
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
